<template>
  <PageNotFound v-if="showNotFound" />
  <v-container v-else class="mb-4 px-8 pb-6 mt-n4">
    <v-row class="mt-12">
      <v-col cols="12" sm="8">
        <ChallengeViewHeader
          class="mb-2"
          :challenge="challenge"
          :date="timeRemaining"
          :revs="$t('common.revWithCount', { count: challenge.totalRevs })"
          :challengeId="challengeId"
          :backgroundUrl="challenge.photo.localLandscapePath"
          :isChallengeActive="isChallengeActive"
          :canEdit="canEdit"
        />

        <EmptyPageStateManager
          v-if="revs.length === 0 || isLoading"
          :imageSource="image"
          :title="$t('rev.no_revs_found_for_this_challenge')"
          :isLoading="isLoading"
        />

        <v-row v-else>
          <v-col
            v-for="(item, index) in revs"
            md="4"
            sm="6"
            xs="12"
            :key="index"
          >
            <RevCard class="mt-4" :rev="item" showValidationIcons />
          </v-col>

          <template v-if="totalPages > 1 && !isLoading">
            <v-row justify="center">
              <v-col cols="8">
                <v-container class="max-width">
                  <v-pagination
                    circle
                    class="my-4"
                    total-visible="10"
                    :length="totalPages"
                    :value="currentPage"
                    @input="goToPage"
                  ></v-pagination>
                </v-container>
              </v-col>
            </v-row>
          </template>
        </v-row>
      </v-col>
      <v-col cols="12" sm="4">
        <Sidebar
          v-if="challenge"
          modelName="challenges"
          tourIdPrefix="challenge-view"
          :modelId="challengeId"
          :challenge="challenge"
          :hasCommentsActive="true"
          :canEdit="canEdit"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { utils } from 'frontend-js-shared'

import Sidebar from '@/containers/SideBar.vue'
import ChallengeViewHeader from './ChallengeViewHeader.vue'
import RevCard from '@/components/RevCard/RevCard.vue'
import EmptyPageStateManager from '@/components/utils/EmptyPageStateManager.vue'
import PageNotFound from '@/components/utils/PageNotFound.vue'
import _image from '@/assets/images/empty-states/empty-state-challenge.png'

import challengePermissions from '@/mixins/challengePermissions.vue'
import mixin from '@/mixins/mixin.vue'

export default {
  components: {
    Sidebar,
    ChallengeViewHeader,
    EmptyPageStateManager,
    RevCard,
    PageNotFound,
  },

  data() {
    return {
      showNotFound: false,
      image: _image,
      DEFAULT_SIZE: 12,
      totalRecordsInData: 0,
      currentPage: 1,
      organizationId: this.getRouteParams().organizationId,
      challengeId: this.getRouteParams().challengeId,
    }
  },

  mixins: [challengePermissions, mixin],

  mounted() {
    this.getChallengeData()
  },

  watch: {
    $route(to, from) {
      if (from.params.challengeId !== to.params.challengeId) {
        this.challengeId = to.params.challengeId
        return this.getChallengeData()
      }
    },
  },

  computed: {
    isChallengeActive() {
      const startAt = this.challenge?.startAt
      const endAt = this.challenge?.endAt
      return utils.dates.isActiveDateRange(startAt, endAt)
    },

    canEdit() {
      return this.canEditChallenge(this.challenge)
    },

    isMultiselection() {
      return (
        this.challenge.countries.length > 1 || this.challenge.sites.length > 1
      )
    },

    challenge() {
      return this.$store.state.challenge.challenge
    },

    revs() {
      return this.$store.state.challenge.revs
    },

    isLoading() {
      return this.$store.state.loader.showing
    },

    challengeHasRevs() {
      return this.revs.length !== 0
    },

    totalPages() {
      return Math.ceil(this.totalRecordsInData / this.DEFAULT_SIZE)
    },

    timeRemaining() {
      return this.$t('common.ends_in_dayWithCount', {
        count: this.getTimeRemaining(),
      })
    },
  },

  beforeDestroy() {
    this.$store.dispatch('clearChallenge')
  },

  methods: {
    ...mapActions({
      getChallenge: 'getChallenge',
      getChallengeRevs: 'getChallengeRevs',
      clearChallenge: 'clearChallenge',
    }),

    getTimeRemaining() {
      if (this.challenge.endAt) {
        const t = Date.parse(this.challenge.endAt) - Date.parse(new Date())
        return Math.floor(t / (1000 * 60 * 60 * 24))
      }
    },

    goToPage(page) {
      this.currentPage = page
      this.getChallengeRevsData()
      this.$emit('goToPage', page)
    },

    getChallengeData(
      challengeId = this.getRouteParams().challengeId,
      organizationId = this.getRouteParams().organizationId
    ) {
      this.clearChallenge()
      return this.getChallenge({
        organizationId,
        challengeId,
      })
        .then(() => {
          this.getChallengeRevsData(challengeId, organizationId)
        })
        .catch(() => {
          this.showNotFound = true
        })
    },

    getChallengeRevsData(
      challengeId = this.getRouteParams().challengeId,
      organizationId = this.getRouteParams().organizationId,
      page = this.currentPage
    ) {
      const options = {
        organizationId: organizationId,
        challengeid: challengeId,
        showLoading: true,
        page: page,
        limit: this.DEFAULT_SIZE,
      }
      return this.getChallengeRevs(options)
        .then(revs => {
          this.totalRecordsInData = revs.totalRecordsInData
        })
        .catch(err => {
          this.showError(err.message)
        })
    },
  },

  beforeRouteUpdate(to, _from, next) {
    let organizationId = to.params.organizationId
    let challengeId = to.params.challengeId

    this.getChallengeData(challengeId, organizationId)
    return next()
  },
}
</script>
